import { EventType } from "@/analytics/analytics-events";
import { DeleteScanSelectionProperties } from "@/analytics/staging-area-workflows-events-properties";
import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import { removeEntities } from "@/data-preparation-tool/store/revision/revision-slice";
import { IconText } from "@/registration-tools/common/icon-text";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { useToast } from "@faro-lotv/app-component-toolbox";
import { deleteCaptureTreeEntities } from "@faro-lotv/data-management";
import { FaroDialog, FaroText, NoTranslate } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  CaptureApiClient,
  CaptureTreeEntityRevision,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Divider } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useMemo, useState, type JSX } from "react";
import { useSelectedEntitiesDescription } from "../../../hooks/use-selected-entities-description";
import {
  selectEntitiesRecursive,
  selectRelevantCaptures,
  selectRelevantClusters,
  selectSelectedEntitiesRecursive,
} from "../../store/data-preparation-ui/data-preparation-ui-selectors";
import { setSelectedEntityIds } from "../../store/data-preparation-ui/data-preparation-ui-slice";
import { EntityIcon } from "../scan-tree/entity-icon";

type DeleteEntitiesDialogProps = {
  /**
   * Entity which was initiated to be deleted through context menu.
   * When no entity is provided, it means the deletion was initiated through
   * the contextual toolbar and all selected entities should be considered for deletion.
   */
  entity?: CaptureTreeEntityRevision;
  /** Whether the deletion dialog is open */
  isDeletionDialogOpen: boolean;
  /** Function to close the deletion dialog */
  onClose(): void;
};

/**
 * A dialog to confirm the deletion of scans and cluster entities.
 * This dialog is used for deletion in both the context menu and the toolbar.
 * It determines which entities to delete based on the provided `entity` prop
 * or the current selection in the state.
 *
 * @returns A JSX element representing the delete scans dialog, or null if the dialog is not open.
 */
export function DeleteEntitiesDialog({
  entity,
  isDeletionDialogOpen,
  onClose,
}: DeleteEntitiesDialogProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();
  const { openToast } = useToast();
  const [isDeletingScans, setIsDeletingScans] = useState(false);

  const affectedEntities = useAppSelector(
    entity
      ? selectEntitiesRecursive([entity.id])
      : selectSelectedEntitiesRecursive,
  );

  const affectedClusters = useAppSelector(
    selectRelevantClusters(affectedEntities),
  );
  const affectedCaptures = useAppSelector(
    selectRelevantCaptures(affectedEntities),
  );

  const userSelectedEntities = useMemo(
    () => [...affectedClusters, ...affectedCaptures],
    [affectedClusters, affectedCaptures],
  );

  const entitiesSelectedDescription = useSelectedEntitiesDescription({
    scansSelectedCount: affectedCaptures.length,
    clustersSelectedCount: affectedClusters.length,
    separator: " and ",
    selectedPosition: "none",
  });

  /**
   * Deletes the selected entities and updates the state accordingly.
   * This function is called when the user confirms the deletion.
   */
  const deleteScans = useCallback(async () => {
    if (isDeletingScans) {
      return;
    }
    try {
      setIsDeletingScans(true);
      // Delete the selected entities from the project API
      await deleteCaptureTreeEntities(
        projectApiClient,
        affectedEntities,
        /* createdByClient */ CaptureApiClient.registrationFrontend,
      );
      dispatch(removeEntities(affectedEntities));
      dispatch(setSelectedEntityIds([]));

      Analytics.track<DeleteScanSelectionProperties>(
        EventType.deleteScanSelection,
        {
          via: "context toolbar",
          selectedScanCount: affectedCaptures.length,
        },
      );

      // Reload data from API
      await dispatch(loadRevisionDataThunk({ projectApiClient }));
      onClose();
    } catch (error) {
      console.error("Error deleting items:", error);
      openToast({
        title: "Deletion Error",
        message: "An error occurred while deleting the items.",
        variant: "error",
      });
    } finally {
      setIsDeletingScans(false);
    }
  }, [
    affectedEntities,
    dispatch,
    onClose,
    projectApiClient,
    openToast,
    isDeletingScans,
    affectedCaptures.length,
  ]);

  return (
    <FaroDialog
      variant="danger"
      title={
        userSelectedEntities.length === 1 ? "Delete item?" : "Delete items?"
      }
      confirmText="Delete"
      open={isDeletionDialogOpen}
      onConfirm={deleteScans}
      onClose={onClose}
      onCancel={onClose}
      isConfirmDisabled={!userSelectedEntities.length || isDeletingScans}
      showSpinner={isDeletingScans}
      showXButton
    >
      <FaroText variant="bodyM">
        The {userSelectedEntities.length === 1 ? "item" : "items"} will be
        removed from your draft.
      </FaroText>

      <Divider sx={{ my: 2 }} />

      <FaroText variant="bodyM">{entitiesSelectedDescription}</FaroText>

      {userSelectedEntities.length > 0 && (
        <Stack sx={{ gap: 1, mt: 1 }}>
          {userSelectedEntities.map((entity) => (
            <IconText
              key={entity.id}
              icon={
                <EntityIcon entity={entity} sx={{ fontSize: "1.125rem" }} />
              }
              variant="bodyM"
              text={<NoTranslate>{entity.name}</NoTranslate>}
            />
          ))}
        </Stack>
      )}
    </FaroDialog>
  );
}
